import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SocketService } from '../socket.service';
import { TimelogService } from '../timelog.service';

export interface TimerState {
  timerId?: string;
  isRunning: boolean;
  isPaused: boolean;
  elapsedTime: number;
  taskName: string;
  projectName: string;
  projectId: string;
  taskId: string;
  taskNumber: string;
  accumulatedTime?: number;
  lastUpdated?: number;
}

export interface Timer {
  timerId: string;
  taskId: string;
  taskName: string;
  taskNumber: string;
  projectId: string;
  projectName: string;
  status: 'RUNNING' | 'PAUSED' | 'STOPPED';
  accumulatedTime: number;
  elapsedTime: number;
  currentSessionStart?: Date;
  createdAt: Date;
  updatedAt: Date;
}

const initialState: TimerState = {
  isRunning: false,
  isPaused: false,
  elapsedTime: 0,
  taskName: '',
  projectName: '',
  projectId: '',
  taskId: '',
  taskNumber: '',
};

@Injectable({
  providedIn: 'root',
})
export class TimerService {
  private worker: Worker | null = null;
  private timerState = new BehaviorSubject<TimerState>(initialState);
  public timerState$ = this.timerState.asObservable();

  // Multi-timer support
  private timersSubject = new BehaviorSubject<Timer[]>([]);
  public timers$ = this.timersSubject.asObservable();

  private readonly STORAGE_KEY = 'pmt_timer_state';
  private readonly THEME_KEY = 'pmt_timer_theme';
  private readonly API_URL = `${environment.baseUrl}task-timer`;

  private isModernThemeSubject = new BehaviorSubject<boolean>(true);
  public isModernTheme$ = this.isModernThemeSubject.asObservable();

  constructor(
    private http: HttpClient,
    private socketService: SocketService,
    private timelogService: TimelogService
  ) {
    this.initWorker();
    this.loadTheme();
    this.loadActiveTimerFromBackend();
    this.loadActiveTimers();
    this.initSocketListeners();
  }

  private loadTheme() {
    const savedTheme = localStorage.getItem(this.THEME_KEY);
    if (savedTheme !== null) {
      this.isModernThemeSubject.next(savedTheme === 'true');
    }
  }

  toggleTheme() {
    const newTheme = !this.isModernThemeSubject.value;
    this.isModernThemeSubject.next(newTheme);
    localStorage.setItem(this.THEME_KEY, String(newTheme));
  }

  private saveState(state: TimerState) {
    localStorage.setItem(
      this.STORAGE_KEY,
      JSON.stringify({
        ...state,
        lastUpdated: Date.now(),
      })
    );
  }

  /**
   * Load active timer from backend on initialization
   */
  private loadActiveTimerFromBackend() {
    this.http.get<any>(`${this.API_URL}/active`).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const timer = response.data;
          const state: TimerState = {
            timerId: timer.timerId,
            isRunning: timer.status === 'RUNNING' || timer.status === 'PAUSED',
            isPaused: timer.status === 'PAUSED',
            elapsedTime: timer.elapsedTime || timer.accumulatedTime,
            taskName: timer.taskName,
            projectName: timer.projectName,
            projectId: timer.projectId,
            taskId: timer.taskId,
            taskNumber: timer.taskNumber,
            accumulatedTime: timer.accumulatedTime,
          };

          this.timerState.next(state);
          this.saveState(state);

          if (state.isRunning && !state.isPaused) {
            this.startInterval();
          }

          console.log('Loaded active timer from backend:', state);
        } else {
          console.log('No active timer found on backend');
        }
      },
      error: (error) => {
        console.error('Failed to load active timer from backend:', error);
        // Fallback to localStorage if backend fails
        this.loadStateFromLocalStorage();
      },
    });
  }

  /**
   * Fallback: Load state from localStorage
   */
  private loadStateFromLocalStorage() {
    const savedState = localStorage.getItem(this.STORAGE_KEY);
    if (savedState) {
      try {
        const parsedState: TimerState = JSON.parse(savedState);
        console.log('Loaded state from localStorage:', parsedState);

        if (
          parsedState.isRunning &&
          !parsedState.isPaused &&
          parsedState.lastUpdated
        ) {
          const now = Date.now();
          const timeDiff = Math.floor((now - parsedState.lastUpdated) / 1000);
          parsedState.elapsedTime += timeDiff;

          this.timerState.next(parsedState);
          this.startInterval();
        } else {
          this.timerState.next(parsedState);
        }
      } catch (e) {
        console.error('Failed to load timer state from localStorage', e);
        this.timerState.next(initialState);
      }
    }
  }

  /**
   * Initialize socket event listeners for real-time timer updates
   */
  private initSocketListeners() {
    this.socketService.on('timer:started', (data) => {
      console.log('[TimerService] Timer started:', data);
      this.loadActiveTimers();
    });

    this.socketService.on('timer:paused', (data) => {
      console.log('[TimerService] Timer paused:', data);
      this.loadActiveTimers();
    });

    this.socketService.on('timer:resumed', (data) => {
      console.log('[TimerService] Timer resumed:', data);
      this.loadActiveTimers();
    });

    this.socketService.on('timer:stopped', (data) => {
      console.log('[TimerService] Timer stopped:', data);
      this.loadActiveTimers();
    });
  }

  /**
   * Load all active (non-STOPPED) timers from backend
   */
  loadActiveTimers() {
    this.http.get<any>(`${this.API_URL}/active`).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const timers = Array.isArray(response.data)
            ? response.data
            : [response.data];
          this.timersSubject.next(timers);
          // Register all running timers in the worker
          timers.forEach((t: Timer) => {
            if (t.status === 'RUNNING') {
              this.worker?.postMessage({ type: 'start', timerId: t.timerId, elapsedTime: t.elapsedTime || 0 });
            } else {
              this.worker?.postMessage({ type: 'stop', timerId: t.timerId });
            }
          });
        } else {
          this.timersSubject.next([]);
        }
      },
      error: (error) => {
        console.error('[TimerService] Failed to load active timers:', error);
      },
    });
  }

  /**
   * Start a new timer
   */
  startTimer(
    projectId: string,
    projectName: string,
    taskId: string,
    taskNumber: string,
    taskName: string
  ) {
    console.log('TimerService.startTimer called with:', {
      projectId,
      projectName,
      taskId,
      taskNumber,
      taskName,
    });

    const payload = {
      projectId,
      projectName,
      taskId,
      taskNumber,
      taskName,
    };

    this.http.post<any>(`${this.API_URL}/start`, payload).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const timer = response.data;
          const state: TimerState = {
            timerId: timer.timerId,
            isRunning: true,
            isPaused: false,
            elapsedTime: timer.elapsedTime || 0,
            taskName: timer.taskName,
            projectName: timer.projectName,
            projectId: timer.projectId,
            taskId: timer.taskId,
            taskNumber: timer.taskNumber,
            accumulatedTime: timer.accumulatedTime,
          };

          this.timerState.next(state);
          this.saveState(state);
          this.startInterval();

          // Handle auto-paused timers notification
          if (timer.pausedTimers && timer.pausedTimers.length > 0) {
            console.log(`Auto-paused ${timer.pausedTimers.length} timer(s)`);
          }

          console.log('Timer started successfully:', state);
          this.loadActiveTimers(); // Refresh active timers list
        }
      },
      error: (error) => {
        console.error('Failed to start timer:', error);
        // TODO: Show error notification to user
      },
    });
  }

  /**
   * Pause the current timer
   */
  /**
   * Pause a timer (defaults to current active timer)
   */
  pauseTimer(timerId?: string, afterPauseCallback?: () => void) {
    const currentState = this.timerState.value;
    const targetTimerId = timerId || currentState.timerId;

    if (!targetTimerId) {
      console.warn('No timerId provided to pause');
      return;
    }

    this.http
      .post<any>(`${this.API_URL}/${targetTimerId}/pause`, {})
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Only update local state if we paused the currently tracked timer
            if (targetTimerId === currentState.timerId) {
              this.stopInterval();
              const newState = {
                ...currentState,
                isPaused: true,
                elapsedTime:
                  response.data.elapsedTime || response.data.accumulatedTime,
                accumulatedTime: response.data.accumulatedTime,
              };
              this.timerState.next(newState);
              this.saveState(newState);
            }

            console.log('Timer paused successfully:', targetTimerId);
            this.loadActiveTimers(); // Refresh active timers list
            afterPauseCallback?.bind(this)();
          }
        },
        error: (error) => {
          console.error('Failed to pause timer:', error);
        },
      });
  }

  /**
   * Resume a timer (defaults to current active timer)
   */
  resumeTimer(timerId?: string) {
    const currentState = this.timerState.value;
    const targetTimerId = timerId || currentState.timerId;

    if (!targetTimerId) {
      console.warn('No timerId provided to resume');
      return;
    }

    this.http
      .post<any>(`${this.API_URL}/${targetTimerId}/resume`, {})
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            const timer = response.data;

            // Only update local state if we resumed the currently tracked timer
            if (targetTimerId === currentState.timerId) {
              const newState = {
                ...currentState,
                isPaused: false,
                elapsedTime: timer.elapsedTime || timer.accumulatedTime,
                accumulatedTime: timer.accumulatedTime,
              };
              this.timerState.next(newState);
              this.saveState(newState);
              this.startInterval();
            }

            // Handle auto-paused timers notification
            if (timer.pausedTimers && timer.pausedTimers.length > 0) {
              console.log(`Auto-paused ${timer.pausedTimers.length} timer(s)`);
            }

            console.log('Timer resumed successfully:', targetTimerId);
            this.loadActiveTimers(); // Refresh active timers list
          }
        },
        error: (error) => {
          console.error('Failed to resume timer:', error);
        },
      });
  }

  /**
   * Stop a timer (defaults to current active timer)
   */
  stopTimer(timerId?: string) {
    const currentState = this.timerState.value;
    const targetTimerId = timerId || currentState.timerId;

    if (!targetTimerId) {
      // No backend timer, just clear local state if we were trying to stop current
      if (!timerId) {
        this.stopInterval();
        this.timerState.next(initialState);
        localStorage.removeItem(this.STORAGE_KEY);
      }
      return;
    }

    this.http.post<any>(`${this.API_URL}/${targetTimerId}/stop`, {}).subscribe({
      next: (response) => {
        // Only clear local state if we stopped the currently tracked timer
        if (targetTimerId === currentState.timerId) {
          this.stopInterval();
          this.timerState.next(initialState);
          localStorage.removeItem(this.STORAGE_KEY);
        }
        this.loadActiveTimers(); // Refresh active timers list
      },
      error: (error) => {
        console.error('Failed to stop timer:', error);
      },
    });
  }

  /**
   * Delete a timer
   */
  deleteTimer(timerId: string): Observable<any> {
    return this.http.delete<any>(`${this.API_URL}/${timerId}`).pipe(
      tap(() => {
        // Remove timer from local state
        this.removeTimerFromState(timerId);
      }),
      catchError((error) => {
        console.error('Error deleting timer:', error);

        // If timer not found (404 or "Timer not found" message), remove it from local state anyway
        // since it's already gone from the server
        if (error.status === 404 || error.error?.message === 'Timer not found') {
          this.removeTimerFromState(timerId);
        }

        return throwError(() => error);
      })
    );
  }

  private removeTimerFromState(timerId: string): void {
    // Stop worker tick for this timer immediately
    this.worker?.postMessage({ type: 'stop', timerId });

    // Remove timer from local timers list
    const currentTimers = this.timersSubject.value;
    const updatedTimers = currentTimers.filter(t => t.timerId !== timerId);
    this.timersSubject.next(updatedTimers);

    // If we deleted the currently tracked single-timer, clear that state too
    const currentState = this.timerState.value;
    if (timerId === currentState.timerId) {
      this.stopInterval();
      this.timerState.next(initialState);
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Update timer metadata (project/task)
   */
  updateTimer(timerId: string, updateData: any): Observable<any> {
    return this.http.patch<any>(`${this.API_URL}/${timerId}/update`, updateData).pipe(
      tap((response) => {
        if (response.success) {
          // Refresh active timers list to get updated data
          this.loadActiveTimers();

          // If this is the currently tracked timer, update its state
          const currentState = this.timerState.value;
          if (timerId === currentState.timerId) {
            this.timerState.next({
              ...currentState,
              taskName: updateData.taskName,
              taskNumber: updateData.taskNumber,
              projectName: updateData.projectName,
              projectId: updateData.projectId,
              taskId: updateData.taskId,
            });
            this.saveState(this.timerState.value);
          }
        }
      }),
      catchError((error) => {
        console.error('Error updating timer:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Initialize the Web Worker for background-safe tick counting.
   */
  private initWorker() {
    if (typeof Worker === 'undefined') return;

    this.worker = new Worker(new URL('./timer.worker', import.meta.url), { type: 'module' });

    this.worker.onmessage = ({ data }) => {
      if (data.type !== 'tick') return;

      // Update single timer state
      const state = this.timerState.value;
      if (state.isRunning && !state.isPaused && state.timerId) {
        const update = data.updates.find((u: any) => u.timerId === state.timerId);
        if (update) {
          const newState = { ...state, elapsedTime: update.elapsedTime };
          this.timerState.next(newState);
          this.saveState(newState);
        }
      }

      // Update multi-timer list
      const currentTimers = this.timersSubject.value;
      if (currentTimers.length === 0) return;
      let hasUpdates = false;
      const updatedTimers = currentTimers.map((timer) => {
        if (timer.status === 'RUNNING') {
          const update = data.updates.find((u: any) => u.timerId === timer.timerId);
          if (update) {
            hasUpdates = true;
            return { ...timer, elapsedTime: update.elapsedTime };
          }
        }
        return timer;
      });
      if (hasUpdates) this.timersSubject.next(updatedTimers);
    };
  }

  private startInterval() {
    const state = this.timerState.value;
    this.worker?.postMessage({ type: 'start', timerId: state.timerId, elapsedTime: state.elapsedTime });
  }

  private stopInterval() {
    const state = this.timerState.value;
    if (state.timerId) {
      this.worker?.postMessage({ type: 'stop', timerId: state.timerId });
    }
  }

  pauseTimerAndOpenTimeLogModal(timerId?: string) {
    this.pauseTimer(timerId, () => {
      this.timelogService.openTimeLogModalForTimer(timerId);
    });
  }
}
